import { useState } from "react";
import { switchLocale as localeHref } from "../i18n/language-switcher";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { SUPPORTED_LOCALES, switchLanguage, type SupportedLocale } from "../../lib/i18n";
import { CURRENCY_CODES, currencySymbolFor, useCurrency, type CurrencyCode } from "../../lib/currency";
import { currencyLabel } from "../../lib/format";
import { useSavePreferences } from "../../features/account/hooks/use-preference-sync";
import { FlagIcon, type FlagCode } from "./flag-icons";
import { floatPanelStyle, popOrigin, useMenu, usePresence, type MenuGroup } from "./use-presence";
import { BEVEL_CHIP_ON, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";

const NAVY = "#110E39";
const GOLD_DARK = "#8A6425";
const MUTED = "#5a5d77";
const HAIRLINE = "rgba(17,14,57,0.09)";
const HAIRLINE_STRONG = "rgba(17,14,57,0.13)";
const SOFT_TINT = "rgba(17,14,57,0.055)";
/** The pressed half of the pill and the lit tab: the soft-bevel navy face, no drop shadow. */
const NAVY_ON = { background: BEVEL_NAVY_FACE, color: "#fff", boxShadow: BEVEL_CHIP_ON };

type LocaleMeta = { native: string; country: FlagCode };
const LOCALE_META: Record<SupportedLocale, LocaleMeta> = {
  en: { native: "English", country: "gb" },
  fr: { native: "Français", country: "fr" },
  ar: { native: "العربية", country: "ma" },
  es: { native: "Español", country: "es" },
  it: { native: "Italiano", country: "it" },
  de: { native: "Deutsch", country: "de" },
  nl: { native: "Nederlands", country: "nl" },
};

type Panel = "lang" | "currency";

/**
 * The two-half pill: flag + code opens the Language tab, coin + code opens
 * Currency. One menu in the bar's group, so tapping either half again, any
 * other trigger, or outside the panel closes it.
 */
export function LocaleCurrencySwitcher({ group }: { group?: MenuGroup }) {
  const { t, i18n: i18nInstance } = useTranslation();
  const navigate = useNavigate();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const currentLang = (lang ?? "en") as SupportedLocale;
  const { currency, setCurrency } = useCurrency();
  // Only the label localizes: "MAD" reads "درهم" on Arabic pages.
  const currencyText = currencyLabel(currency, currentLang);
  const { save } = useSavePreferences();
  const menu = useMenu(group, "locale");
  const [panel, setPanel] = useState<Panel>("lang");
  const presence = usePresence(menu.open);

  function switchLocale(next: SupportedLocale) {
    // Loads the strings before switching; the layout's beforeLoad shares the
    // same in-flight request, so the navigation below cannot beat it.
    void switchLanguage(next);
    void save({ language: next });
    // The whole href, not the pathname: the query holds the guest's dates,
    // party, filters and booking choices.
    const { pathname, search, hash } = window.location;
    navigate({ href: localeHref(`${pathname}${search}${hash}`, next), resetScroll: false });
    menu.close();
  }

  function pickCurrency(code: CurrencyCode) {
    setCurrency(code);
    void save({ currency: code });
    menu.close();
  }

  function openHalf(which: Panel) {
    if (menu.open && panel === which) {
      menu.close();
      return;
    }
    setPanel(which);
    menu.setOpen(true);
  }

  const halfOn = (which: Panel) => menu.open && panel === which;

  return (
    <div ref={menu.ref} className="relative shrink-0">
      <div
        className="inline-flex h-9 items-stretch rounded-full"
        style={{ background: SOFT_TINT }}
        role="group"
        aria-label={t("common.locale_title", {
          language: t(`common.language_${currentLang}`),
          currency: currencyText,
        })}
      >
        <button
          type="button"
          onClick={() => openHalf("lang")}
          aria-haspopup="listbox"
          aria-expanded={halfOn("lang")}
          className="mj-press flex h-9 items-center gap-1.5 rounded-full ps-2 pe-2 text-[11.5px] font-bold uppercase"
          style={{ letterSpacing: "0.06em", ...(halfOn("lang") ? NAVY_ON : { color: NAVY }) }}
        >
          <span className="flex h-4 w-4 shrink-0 overflow-hidden rounded-full" style={{ boxShadow: `0 0 0 1px ${HAIRLINE}` }}>
            <FlagIcon code={LOCALE_META[currentLang].country} size={16} />
          </span>
          <span>{currentLang}</span>
        </button>
        <span aria-hidden className="my-auto h-4 w-px shrink-0" style={{ background: HAIRLINE_STRONG }} />
        <button
          type="button"
          onClick={() => openHalf("currency")}
          aria-haspopup="listbox"
          aria-expanded={halfOn("currency")}
          className="mj-press flex h-9 items-center rounded-full ps-2 pe-2.5 text-[11.5px] font-bold uppercase"
          style={{ letterSpacing: "0.06em", ...(halfOn("currency") ? NAVY_ON : { color: NAVY }) }}
        >
          {/* Just the code: the coins icon read as a link (owner, 2026-10-11). */}
          <span>{currencyText}</span>
        </button>
      </div>

      {presence.mounted ? (
        <div
          ref={presence.ref}
          data-state={presence.state}
          data-instant={menu.instant ? "true" : undefined}
          className={`mj-pop absolute end-0 top-[calc(100%+8px)] z-[var(--z-dropdown)] w-[260px] p-1.5 ${
            presence.state === "closed" ? "pointer-events-none" : ""
          }`}
          style={{ ...floatPanelStyle(18), transformOrigin: popOrigin("end", i18nInstance.dir()) }}
        >
          {/* Both lists live behind one pair of tabs, so the guest can move
              between language and currency without closing the panel. */}
          <div role="tablist" aria-label={t("common.regional_preferences")} className="mb-1.5 flex rounded-full p-1" style={{ background: SOFT_TINT }}>
            {(["lang", "currency"] as const).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={panel === key}
                onClick={() => setPanel(key)}
                className="mj-press min-h-7 flex-1 rounded-full py-1.5 text-[12px] font-bold"
                style={panel === key ? NAVY_ON : { color: MUTED }}
              >
                {key === "lang" ? t("common.language") : t("common.currency")}
              </button>
            ))}
          </div>
          {panel === "lang" ? (
            <div role="listbox" aria-label={t("common.language")}>
              {SUPPORTED_LOCALES.map((l) => {
                const meta = LOCALE_META[l];
                const active = l === currentLang;
                return (
                  <button
                    key={l}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => switchLocale(l)}
                    className="flex w-full items-center gap-3 rounded-[12px] px-2.5 py-2 text-start hover:bg-black/5"
                    style={{ background: active ? SOFT_TINT : undefined }}
                  >
                    <span className="flex h-7 w-7 shrink-0 overflow-hidden rounded-full">
                      <FlagIcon code={meta.country} size={28} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13.5px] font-bold" style={{ color: NAVY }}>
                        {meta.native}
                      </span>
                      <span className="block text-[11px]" style={{ color: MUTED }}>
                        {t(`common.language_${l}`)}
                      </span>
                    </span>
                    {active ? <Check size={14} strokeWidth={2.5} className="shrink-0" style={{ color: GOLD_DARK }} aria-hidden /> : null}
                  </button>
                );
              })}
            </div>
          ) : (
            <div role="listbox" aria-label={t("common.currency")}>
              {CURRENCY_CODES.map((code) => {
                const active = code === currency;
                return (
                  <button
                    key={code}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => pickCurrency(code)}
                    className="flex w-full items-center gap-3 rounded-[12px] px-2.5 py-2 text-start hover:bg-black/5"
                    style={{ background: active ? SOFT_TINT : undefined }}
                  >
                    <span
                      aria-hidden
                      className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[12px] font-bold"
                      style={{ background: SOFT_TINT, color: NAVY }}
                    >
                      {currencySymbolFor(code)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13.5px] font-bold" style={{ color: NAVY }}>
                        {code}
                      </span>
                      <span className="block text-[11px]" style={{ color: MUTED }}>
                        {t(`common.currency_${code}`)}
                      </span>
                    </span>
                    {active ? <Check size={14} strokeWidth={2.5} className="shrink-0" style={{ color: GOLD_DARK }} aria-hidden /> : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
